import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CartService, CartItem } from '../../services/cart.service';
import { ReservationsService } from '../../services/reservations.service';
import { AuthService } from '../../services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { ResponseModalComponent } from '../response-modal/response-modal.component';
import { QuantityStepperComponent } from '../quantity-stepper/quantity-stepper.component';
import { ButtonComponent } from '../button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-cart',
  standalone: true,
  imports: [CommonModule, FormsModule, QuantityStepperComponent, ButtonComponent, TranslateModule],
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.css']
})
export class CartComponent implements OnInit {
  cartItems: CartItem[] = [];
  isCartValid: boolean = false;
  isProcessing = false;
  errorMessage = '';
  expandedItems = new Set<number>();
  showLoginModal = false;

  invalidFields: { itemIndex: number, nameIndex: number }[] = [];

  constructor(
    public cartService: CartService,
    private reservationsService: ReservationsService,
    private router: Router,
    private authService: AuthService,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  getAttendeePlaceholder(num: number): string {
    return `${this.translate.instant('cart.attendeePlaceholder')} ${num}`;
  }

  ngOnInit(): void {
    this.cartService.getCart().subscribe(items => {
      this.cartItems = items;
      this.checkValidity();
    });
  }

  checkValidity(): void {
    if (this.cartItems.length === 0) {
      this.isCartValid = false;
      return;
    }

    this.isCartValid = this.cartItems.every(item => {
      const quantityMatch = item.visitorNames.length === item.quantity;
      const allNamesFilled = item.visitorNames.every(name => name && name.trim().length > 0);
      
      if (!quantityMatch) console.warn('Quantity mismatch', item);
      if (!allNamesFilled) console.warn('Names not filled', item);
      
      return quantityMatch && allNamesFilled;
    });
    
    console.log('Cart validity check:', this.isCartValid);
  }

  getSubtotal(item: CartItem): number {
    return item.pricePerUnit * item.quantity;
  }

  getTax(item: CartItem): number {
    const subtotal = this.getSubtotal(item);
    return subtotal * 0.07;
  }

  getItemTotal(item: CartItem): number {
    return this.getSubtotal(item) + this.getTax(item);
  }

  getCartSubtotal(): number {
    return this.cartItems.reduce((total, item) => total + this.getSubtotal(item), 0);
  }

  getCartTax(): number {
    return this.cartItems.reduce((total, item) => total + this.getTax(item), 0);
  }

  getCartTotal(): number {
    return this.getCartSubtotal() + this.getCartTax();
  }

  updateQuantity(index: number, newQuantity: number): void {
    if (!isNaN(newQuantity) && newQuantity >= 1) {
      this.cartService.updateQuantity(index, newQuantity);
      // Let's clear any field errors if the quantity is changed
      this.invalidFields = [];
    }
  }

  updateVisitorName(itemIndex: number, nameIndex: number, name: string): void {
    const item = this.cartItems[itemIndex];
    const newVisitorNames = [...item.visitorNames];
    newVisitorNames[nameIndex] = name;
    this.cartService.updateVisitorNames(itemIndex, newVisitorNames);
    
    // Clear the error for this specific field when user types
    this.invalidFields = this.invalidFields.filter(field => !(field.itemIndex === itemIndex && field.nameIndex === nameIndex));
  }

  isFieldInvalid(itemIndex: number, nameIndex: number): boolean {
    return this.invalidFields.some(field => field.itemIndex === itemIndex && field.nameIndex === nameIndex);
  }

  removeItem(index: number): void {
    this.cartService.removeItem(index);
    this.invalidFields = this.invalidFields.filter(field => field.itemIndex !== index);
  }

  clearCart(): void {
    if (confirm('¿Estás seguro que deseas vaciar el carrito?')) {
      this.cartService.clearCart();
    }
  }

  proceedToCheckout(): void {
    this.errorMessage = '';
    this.invalidFields = [];

    // Custom stricter validation for attendee names (Name + Last name)
    let hasValidationErrors = false;
    this.cartItems.forEach((item, itemIndex) => {
      item.visitorNames.forEach((name, nameIndex) => {
        // Must contain at least two words with alphanumeric characters
        if (!name || name.trim().split(/\s+/).length < 2) {
          this.invalidFields.push({ itemIndex, nameIndex });
          hasValidationErrors = true;
        }
      });
    });

    if (hasValidationErrors) {
      this.errorMessage = 'Por favor, ingresa el nombre y apellido completo para todos los asistentes marcados en rojo.';
      return;
    }

    if (!this.cartService.isCartValid() || !this.isCartValid) {
      this.errorMessage = 'El carrito no es válido. Revisa las cantidades y asistentes.';
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.showLoginModal = true;
      return;
    }

    this.isProcessing = true;

    // Preparar datos para el API
    const cartData = {
      items: this.cartItems.map(item => {
        if (item.typeId === 1) {
          return {
            typeId: 1,
            eventId: item.eventId,
            quantity: item.quantity,
            visitorNames: item.visitorNames
          };
        } else {
          return {
            typeId: 2,
            selections: item.selections,
            quantity: item.quantity,
            visitorNames: item.visitorNames
          };
        }
      })
    };

    // Llamar al API para crear las reservaciones
    this.reservationsService.processCart(cartData).subscribe({
      next: (response: any[]) => {
        this.isProcessing = false;
        // Navegar a checkout con el locator de la primera reservación (ahora es una sola con items)
        if (response && response.length > 0) {
            this.router.navigate(['/checkout', response[0].locator]);
        }
      },
      error: (error) => {
        this.isProcessing = false;
        this.errorMessage = error.error?.message || 'Error al procesar el carrito. Por favor, intenta de nuevo.';
        console.error('Error processing cart:', error);
      }
    });
  }

  toggleBranches(index: number): void {
    if (this.expandedItems.has(index)) {
      this.expandedItems.delete(index);
    } else {
      this.expandedItems.add(index);
    }
  }

  getCustomRouteExpiry(): Date {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date;
  }

  goToEvents(): void {
    this.router.navigate(['/events']);
  }

  goToNewHop(): void {
    this.router.navigate(['/new-hop']);
  }

  goToHome(): void {
    this.router.navigate(['/']);
  }

  goToLogin(): void {
    this.showLoginModal = false;
    this.router.navigate(['/login'], { queryParams: { returnUrl: '/cart' } });
  }

  goToRegister(): void {
    this.showLoginModal = false;
    this.router.navigate(['/register'], { queryParams: { returnUrl: '/cart' } });
  }

  onlyLetters(event: KeyboardEvent): boolean {
    const input = event.target as HTMLInputElement;
    // Block space if there's already a space in the value
    if (event.key === ' ' && input.value.includes(' ')) {
      event.preventDefault();
      return false;
    }
    // Block space at the start
    if (event.key === ' ' && input.value.length === 0) {
      event.preventDefault();
      return false;
    }
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ ]$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  sanitizeVisitorName(itemIndex: number, nameIndex: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    // Strip invalid chars, then keep only the first space
    const stripped = input.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ ]/g, '');
    const firstSpace = stripped.indexOf(' ');
    const sanitized = firstSpace === -1
      ? stripped
      : stripped.slice(0, firstSpace + 1) + stripped.slice(firstSpace + 1).replace(/ /g, '');
    if (input.value !== sanitized) {
      input.value = sanitized;
    }
    this.updateVisitorName(itemIndex, nameIndex, sanitized);
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }
}
